import React, { useCallback, useEffect, useState } from "react";
import Header from "../app/Header";
import { Container, Form } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Analyses from "./Analyses";
import AdditionalInfos from "./AdditionalInfos";
import BasicInfos from "./BasicInfos";
import TransferButton from "./TransferButton";
import axios from "../../common/api/HttpClient"
import { useLang } from "../../common/hooks/useLang";
import ProductHelper from "../products/ProductHelper";
import { useTranslation } from "react-i18next";
import { NAMESPACE_REMOTE } from "../../common/translations/TranslationHelper";
import TransferResultDialog from "./TransferResultDialog";
import TransferHelper from "./TransferHelper";
import PreviousOrderHelper from "../../common/helpers/PreviousOrderHelper";
import { useToken } from "../../common/hooks/useToken";
import { TOKEN_KEY_FIRM_ID } from "../../common/helpers/TokenHelper"
import "./Order.scss";

export default function Order() {
  let [searchParams] = useSearchParams();
  let treeItemKey = searchParams.get("id");

  const { accessToken } = useToken();
  const { currentLanguage, setLanguageDisabled } = useLang();

  useEffect(() => {
    // Disable language, while order component is visible
    setLanguageDisabled(true);

    return () => {
      // Clean up function: 
      // Enable language again, before the order component leaves the screen
      setLanguageDisabled(false);
    }
  });

  const [standardOrderId, setStandardOrderId] = useState(null);
  const handleStandardOrderChanged = (e) => {
    setStandardOrderId(e.target.value);
  };
  const [standardOrder, setStandardOrder] = useState(null);
  useEffect(() => {
    let notInterrupted = true;

    if (standardOrderId != null) {
      axios({
        method: "get",
        url: `/default-order/${standardOrderId}`,
        params: { sprache: currentLanguage }
      })
        .then((response) => {
          if (notInterrupted) {
            setStandardOrder(response.data);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }

    // If clean-up function executes, the axios call was interrupted
    // and the results can not be rendered in the dom anymore.
    // This may happen, if the user navigates back during the call.
    return () => {
      notInterrupted = false;
    }
  }, [standardOrderId, currentLanguage]);

  const [products, setProducts] = useState([]);
  const [standardOrders, setStandardOrders] = useState([]);
  useEffect(() => {
    let notInterrupted = true;

    setProductsLoading(true);
    const userFirmId = accessToken.decodedToken[TOKEN_KEY_FIRM_ID];
    axios({
      method: "get",
      url: "/product-catalogue",
      params: { firmaId: userFirmId, sprache: currentLanguage }
    })
      .then((response) => {
        if (notInterrupted) {
          const products = response.data.data;
          setProducts(products);

          const filteredStandardOrders = ProductHelper.getStandardOrdersForLevel3Node(products, treeItemKey);
          setStandardOrders(filteredStandardOrders);
          setProductsLoading(false);
        }
      })
      .catch((error) => {
        console.error(error);
        setProductsLoading(false);
      });

    // If clean-up function executes, the axios call was interrupted
    // and the results can not be rendered in the dom anymore.
    // This may happen, if the user navigates back during the call.
    return () => {
      notInterrupted = false;
    }
  }, [treeItemKey, accessToken.decodedToken, currentLanguage]);

  const [customOrderId, setCustomOrderId] = useState(null);
  const handleCustomOrderIdChanged = (e) => {
    setCustomOrderId(e.target.value);
  };

  const [orderPriorities, setOrderPriorities] = useState([]);
  useEffect(() => {
    let notInterrupted = true;

    const domainId = "GFPS.AUFT_PRIO";
    axios({
      method: "get",
      url: `/domain/${domainId}`,
      params: { sprache: currentLanguage }
    })
      .then((response) => {
        if (notInterrupted) {
          setOrderPriorities(response.data.data);
        }
      })
      .catch((error) => {
        console.error(error);
      });

    // If clean-up function executes, the axios call was interrupted
    // and the results can not be rendered in the dom anymore.
    // This may happen, if the user navigates back during the call.
    return () => {
      notInterrupted = false;
    }
  }, [currentLanguage]);

  const [orderPriority, setOrderPriority] = useState(null);
  const handleOrderPriorityChanged = (e) => {
    setOrderPriority(e.target.value);
  };

  const [analyses, setAnalyses] = useState(null);
  const handleAnalysesChanged = useCallback((newAnalyses) => {
    setAnalyses(newAnalyses);
  }, []);

  const [additionalInfos, setAdditionalInfos] = useState(null);
  const handleAdditionalInfosChanged = useCallback((newAdditionalInfos) => {
    setAdditionalInfos(newAdditionalInfos);
  }, []);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const handleTermsAcceptedChanged = (e) => {
    setTermsAccepted(e.target.checked);
  };

  const scrollToFirstInvalid = (formSelector) => {
    const form = document.querySelector(formSelector);
    const invalidInputs = Array.from(form.querySelectorAll(':invalid, .is-invalid, .dynamic-form-control-invalid'));
    // Sort inputs by offset from top of viewport
    invalidInputs.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
    invalidInputs[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  const [analysisSelected, setAnalysisSelected] = useState(undefined);
  useEffect(() => {
    if (analyses !== undefined && analyses !== null) {
      setAnalysisSelected(analyses.filter((p) => p["checked"] === true).length > 0);
    }
  }, [analyses]);

  const [validated, setValidated] = useState(false);
  const [transferActive, setTransferActive] = useState(false);
  const [showTransferResultDialog, setShowTransferResultDialog] = useState(false);
  const [transferRequestData, setTransferRequestData] = useState({});
  const [transferResponse, setTransferResponse] = useState(null);
  const [transferError, setTransferError] = useState(null);
  const handlePlaceOrderClicked = async(e) => {

    e.preventDefault();

    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      scrollToFirstInvalid("#order-form");
      setValidated(true);
      return;
    }

    if (!analysisSelected) {
      return;
    }

    setTransferActive(true);
    setShowTransferResultDialog(true);

    // Transfer to lisa.lims
    try {
      const requestData = TransferHelper.createRequestData(
        standardOrder, customOrderId, orderPriority, analyses, additionalInfos
      )
      setTransferRequestData(requestData);

      const response = await axios({
        method: "post",
        url: "/order-formular",
        data: requestData
      });

      setTransferActive(false);
      setTransferResponse(response);

    } catch (error) {
      setTransferActive(false);
      setTransferError(error);
    }
  }

  const navigate = useNavigate();
  const handleCancelOrderClicked = (e) => {
    navigate("/home", { replace: true });
  }

  const [previousOrder] = useState(() => {
    return PreviousOrderHelper.getCachedOrder();
  });

  const [cachedOrderFirmId, setCachedOrderFirmId] = useState(null);
  useEffect(() => {
    let notInterrupted = true;

    if (previousOrder != null) {
      const standardOrderId = previousOrder.AUFTRAG.standardauftragId;
      axios({
        method: "get",
        url: `/default-order/${standardOrderId}`,
        params: { sprache: currentLanguage }
      })
        .then((response) => {
          if (notInterrupted) {
            setCachedOrderFirmId(response.data.KUNDE.firmaId);
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            console.log("Standardauftrag nicht gefunden oder für Kunde nicht sichtbar.");
          } else {
            console.error(error);
          }
        });
    }

    // If clean-up function executes, the axios call was interrupted
    // and the results can not be rendered in the dom anymore.
    // This may happen, if the user navigates back during the call.
    return () => {
      notInterrupted = false;
    }
  }, [previousOrder, currentLanguage]);

  let preset = searchParams.get("preset");
  const [cachedOrder, setCachedOrder] = useState();
  useEffect(() => {
    if (preset === "true") {
      const userFirmId = accessToken.decodedToken[TOKEN_KEY_FIRM_ID];
      // Preset only, if FIRM_ID matches with the logged in user,
      // because the last order may be created by another logged in user
      if (previousOrder && cachedOrderFirmId && (cachedOrderFirmId === "0" || cachedOrderFirmId === userFirmId)) {
        const matchingStandardOrder = standardOrders.some(s => {
          return s.PARAMETER2 === previousOrder.AUFTRAG.standardauftragId;
        });

        if (matchingStandardOrder === true) {
          setCachedOrder(previousOrder);
        }
      }
    }
  }, [preset, accessToken.decodedToken, previousOrder, cachedOrderFirmId, standardOrders]);

  useEffect(() => {
    if (cachedOrder) {
      setStandardOrderId(cachedOrder["AUFTRAG"].standardauftragId);
      setCustomOrderId(cachedOrder["AUFTRAG"].zusatz1);
      setOrderPriority(cachedOrder["AUFTRAG"].zusatz2);
    }
  }, [cachedOrder]);

  const { t } = useTranslation(NAMESPACE_REMOTE);

  const [isProductsLoading, setProductsLoading] = useState(undefined);

  return (
    <Container className="p-0" fluid>
      <header>
        <Header title={t("PROMPT.WSHOP2.CREATE_ORDER.TITLE")}></Header>
      </header>

      <main>
        <Container className="px-1" fluid>
          <Link to="/products" replace={true}>{t("PROMPT.WSHOP2.CREATE_ORDER.BACK_TO_PRODUCTS")}</Link>
        </Container>

        <Container fluid="md" className="py-3 px-0">
          <Form id="order-form" noValidate validated={validated} onSubmit={(e) => handlePlaceOrderClicked(e)}>
            <BasicInfos
              products={products}
              standardOrders={standardOrders}
              standardOrderId={standardOrderId}
              standardOrder={standardOrder}
              handleStandardOrderChanged={handleStandardOrderChanged}
              customOrderId={customOrderId}
              handleCustomOrderIdChanged={handleCustomOrderIdChanged}
              orderPriorities={orderPriorities}
              orderPriority={orderPriority}
              handleOrderPriorityChanged={handleOrderPriorityChanged}
              isProductsLoading={isProductsLoading}
            />

            <Analyses
              standardOrders={standardOrders}
              standardOrder={standardOrder}
              handleAnalysesChanged={handleAnalysesChanged}
              cachedOrder={cachedOrder}
            />

            <AdditionalInfos
              standardOrder={standardOrder}
              handleAdditionalInfosChanged={handleAdditionalInfosChanged}
              cachedOrder={cachedOrder}
            />

            <TransferButton
              standardOrderId={standardOrderId}
              customOrderId={customOrderId}
              analyses={analyses}
              additionalInfos={additionalInfos}
              termsAccepted={termsAccepted}
              handleTermsAcceptedChanged={handleTermsAcceptedChanged}
              handlePlaceOrderClicked={handlePlaceOrderClicked}
              handleCancelOrderClicked={handleCancelOrderClicked}
              analysisSelected={analysisSelected}
            />
          </Form>

          <TransferResultDialog
            show={showTransferResultDialog}
            onHide={() => setShowTransferResultDialog(false)}
            treeItemKey={treeItemKey}
            transferActive={transferActive}
            request={transferRequestData}
            response={transferResponse}
            error={transferError}
          />
        </Container>
      </main>
    </Container>
  );
}
